/**
 * @file Page lifecycle helpers for the async page modules.
 *
 * Two hazards these solve, both reachable on a normal-latency connection:
 *
 *  1. NAVIGATION RACE. Page renderers await Firestore, then append to the
 *     host. If the user clicks another tab during that await, the router has
 *     already cleared #page and mounted the next page — the late append
 *     stacks old content into the new page and leaks whatever subscriptions
 *     it started. `pageToken()` + `isStale()` make the renderer bail instead.
 *
 *  2. MANUAL RE-MOUNT. A few flows re-render a whole page in place (e.g.
 *     Finish onboarding, after the phase changes). Calling the renderer
 *     directly skips the router's teardown, so the previous mount's
 *     onSnapshot streams stay subscribed and keep re-rendering detached DOM.
 *     `remountPage()` dispatches 'cleanup' first.
 */

/**
 * Snapshot the host's identity + route at render start. Pass the result to
 * isStale() after each await.
 */
export function pageToken(host) {
  return { host, hash: location.hash };
}

/**
 * True when the render this token belongs to is no longer the live one —
 * the host was detached (page cleared/replaced) or the route changed.
 * Callers should return immediately without touching the DOM.
 */
export function isStale(token) {
  if (!token) return false;
  if (token.hash !== location.hash) return true;
  return !token.host?.isConnected;
}

/**
 * Re-render a page in place, firing the current mount's 'cleanup' listeners
 * first so its Firestore subscriptions are released. Use instead of calling
 * a page renderer directly.
 */
export function remountPage(host, renderer, ...args) {
  host.dispatchEvent(new Event('cleanup'));
  return renderer(host, ...args);
}
